// From 'antd', not 'antd/lib/empty': see PostsList.
import { Empty, Skeleton } from 'antd';
import PostsList from 'components/PostsList';
import { colors } from 'design';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import AskMidwifeCard from './AskMidwifeCard';
import CategoryRows from './CategoryRows';
import { useCategorySearch, usePostSearch } from './queries';

const useStyles = createUseStyles({
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  heading: {
    margin: '0 0 8px',
    fontWeight: 500,
  },
  noResults: {
    color: colors.text.secondary,
  },
});

/**
 * Search on the categorised Infos page: the matching categories first (a
 * theme with a single article stays findable through its name and synonyms),
 * then the matching articles.
 */
export default function InfoSearchResults({ query }: { query: string }) {
  const styles = useStyles();
  const { t } = useTranslation();

  const { data: categories, isPending: loadingCategories } = useCategorySearch(query);
  const { data: posts, isPending: loadingPosts } = usePostSearch(query);
  useEffect(() => {
    if (categories && posts) {
      posthog.capture('info.search_performed', {
        search_query: query,
        search_results_count: posts.length,
        search_category_results_count: categories.length,
      });
    }
  }, [query, categories, posts]);

  if (loadingCategories || loadingPosts) {
    return (
      <div className={styles.results}>
        <Skeleton.Input block active style={{ height: 64 }} />
        <Skeleton.Input block active style={{ height: 64 }} />
      </div>
    );
  }

  const categoryHits = categories ?? [];
  const postHits = posts ?? [];
  const nothing = categoryHits.length === 0 && postHits.length === 0;
  const areaOf = new Map(categoryHits.map(hit => [hit.id, hit.area?.name]));

  return (
    <div className={styles.results} data-testid="info-search-results">
      {categoryHits.length > 0 && (
        <div>
          <h4 className={styles.heading}>{t('infoPage.structure.topics')}</h4>
          <CategoryRows
            categories={categoryHits}
            openedFrom="info-search"
            getSubline={hit =>
              areaOf.get(hit.id) ? t('infoPage.structure.inArea', { area: areaOf.get(hit.id) }) : undefined
            }
            testIdPrefix="search-category"
          />
        </div>
      )}
      {postHits.length > 0 && (
        <div>
          <h4 className={styles.heading}>{t('infoPage.structure.articlesTitle')}</h4>
          <PostsList
            posts={postHits}
            posthogOnArticleOpenExtras={{ opened_on_page: 'info-search', found_using_search: query }}
            testIdPrefix="postinlist"
          />
        </div>
      )}
      {nothing && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span className={styles.noResults}>{t('infoPage.structure.noResults')}</span>}
        />
      )}
      <AskMidwifeCard source="info-search" />
    </div>
  );
}
